import { TPod } from '@types';

export enum INVOICE_SOURCE {
	XERO = 'xero',
	NETSUITE = 'netsuite',
}

export enum SHIPPER_INVOICE_STATUS {
    PAYMENT_DISPUTE = 'PAYMENT_DISPUTE',
    SHIPPER_PROMISED_TO_PAY = 'SHIPPER_PROMISED_TO_PAY'
}

export const getShipperInvoiceStatusLabel = (invoiceStatus: SHIPPER_INVOICE_STATUS): string => {
	switch (invoiceStatus) {
	case SHIPPER_INVOICE_STATUS.PAYMENT_DISPUTE:
		return 'Shipper Payment Dispute';
	case SHIPPER_INVOICE_STATUS.SHIPPER_PROMISED_TO_PAY:
		return 'Shipper Promised to Pay';
	default:
		return ''
	}
};

export const shipperInvoiceStatusOptions = [
	{
		label: getShipperInvoiceStatusLabel(SHIPPER_INVOICE_STATUS.PAYMENT_DISPUTE),
		value: SHIPPER_INVOICE_STATUS.PAYMENT_DISPUTE,
	},
	{
		label: getShipperInvoiceStatusLabel(SHIPPER_INVOICE_STATUS.SHIPPER_PROMISED_TO_PAY),
		value: SHIPPER_INVOICE_STATUS.SHIPPER_PROMISED_TO_PAY,
	}
];

export enum CARRIER_INVOICE_STATUS {
    CARRIER_PAYMENT_DISPUTE = 'CARRIER_PAYMENT_DISPUTE',
    CARRIER_DO_NOT_PAY = 'CARRIER_DO_NOT_PAY',
}

export const getCarrierInvoiceStatusLabel = (invoiceStatus: CARRIER_INVOICE_STATUS): string => {
	switch (invoiceStatus) {
	case CARRIER_INVOICE_STATUS.CARRIER_PAYMENT_DISPUTE:
		return 'Carrier Payment Dispute';
	case CARRIER_INVOICE_STATUS.CARRIER_DO_NOT_PAY:
		return 'Do Not Pay';
	default:
		return ''
	}
};

export const carrierInvoiceStatusOptions = [
	{
		label: getCarrierInvoiceStatusLabel(CARRIER_INVOICE_STATUS.CARRIER_PAYMENT_DISPUTE),
		value: CARRIER_INVOICE_STATUS.CARRIER_PAYMENT_DISPUTE,
	},
	{
		label: getCarrierInvoiceStatusLabel(CARRIER_INVOICE_STATUS.CARRIER_DO_NOT_PAY),
		value: CARRIER_INVOICE_STATUS.CARRIER_DO_NOT_PAY,
	}
];


export type TInvoiceCustomer = {
	id: string;
	type: string;
	name: string;
	abn: string;
	companyId: string;
	costCenterId: string;
	externalId: string;
	draftInvoice: boolean;
	rcti: boolean;
	paymentMethod: string | null;
	bankDetails: any[];
	brand: string | null;
	invoiceReference: string | null;
}

export type TInvoiceRateShipment = {
	id: string;
	masterId: string;
	orderNumber: string;
	masterManifestReference: string;
	shipperCompanyId: string;
	shipperCostCenterId: string;
	pickDate: string | null;
	deliveryDate: string | null;
	pickAddress: string | null;
	dropAddress: string | null;
	pod_count: string | null;
	pods: TPod[];
}

export type TInvoiceRates = {
	id: string;
	basePrice: string;
	fuelLevyPercentage: string;
	fuelLevy: string;
	modifiersAmount: string;
	totalAmount: string;
	insuranceAmount: string;
	currency: string;
	modifiers: any[];
	description: string;
	modifiersDescription: string;
	externalId: number;
	shipment: TInvoiceRateShipment;
	type: string | null;
	trackingCategory: string;
	itemCode: string | null;
	insuranceProvider: string;
	modifiersFuelLevy: string;
}

export type TInvoiceDetails = {
	invoice: {
		id: string;
		customer: TInvoiceCustomer;
		payments: any[];
		rates: TInvoiceRates[];
		frequency: string;
		creditTerm: string;
		isPaid: boolean;
		externalId: string;
		isSynced: boolean;
		isEmailSent: boolean;
		isClaim: boolean;
		isRCTI: boolean;
		pdfUrl: string;
		pdf: string;
		externalReference: string;
		totalAmount: string;
		dueDate: string;
		/* Optional, not just nullable: finance-api only started returning this in
		   AP2-1990, so on an older backend the key is absent and reads as undefined. */
		invoiceDate?: string | null;
		receivedAt: string | null;
		createdAt: string;
		updatedAt: string;
		syncedAt: string;
		promotion: any[];
		allowRetry: boolean;
		promotions: any[];
		createdAtRcti: boolean;
		createdAtPaymentMethod: string | null;
		status: string | null;
		paidAt: string | null;
		paymentWithheldAt: string | null;
		subTotalAmount: string;
		fastpayType: string | null;
		source?: INVOICE_SOURCE;
	},
	rates_count: number;
	current_page: number;
	per_page: number;
}

/* The finance audit trail (CON-1409). finance-api-v2 records every step it takes on a
   shipment, manifest, invoice or payment; EOS proxies it back under /api/admin and
   /api/finance. See finance-api-v2's AuditTrailController for the source of this shape. */

export enum FINANCE_AUDIT_SEVERITY {
	INFO = 'info',
	WARNING = 'warning',
	ERROR = 'error'
}

/* How the document being viewed related to the event. 'covered' means it was one of many
   on it — a shipment that was one of 312 lines on a consolidated invoice — which reads
   very differently from an event about the document itself. */
export enum FINANCE_AUDIT_ROLE {
	PRIMARY = 'primary',
	COVERED = 'covered',
	PARENT = 'parent'
}

export type TFinanceAuditActor = {
	/* 'system' is not a gap in the data: scheduled and queue-driven work genuinely has
	   nobody behind it. */
	type: 'user' | 'system' | string;
	id: string | null;
	label: string | null;
}

export type TFinanceAuditEvent = {
	id: number;
	/*
	  `<domain>.<past_tense_verb>`, e.g. `invoice.synced`. Events submitted by EOS, MINT
	  and NOVA are the exception: finance stores them all as `external.event_recorded`
	  and keeps the real action in `context.externalAction`.
	*/
	action: string;
	severity: FINANCE_AUDIT_SEVERITY;
	summary: string | null;
	/* occurredAt orders the story; recordedAt is when finance learned it. They differ when
	   something was backdated or replayed. */
	occurredAt: string;
	recordedAt: string | null;
	sourceSystem: string;
	actor: TFinanceAuditActor;
	correlationId: string | null;
	causationId: number | null;
	state: { from: string | null; to: string | null };
	error: { class: string; message: string | null } | null;
	context: Record<string, unknown>;
	role: FINANCE_AUDIT_ROLE;
	/* The OTHER documents on this event — the record being viewed is left out, since the
	   reader is already looking at it. Lets a shipment row link to the invoice it names. */
	subjects?: {
		type: string;
		reference: string;
		role: FINANCE_AUDIT_ROLE;
	}[];
}

export type TFinanceAuditResponse = {
	data: TFinanceAuditEvent[];
	meta: {
		count: number;
		hasMore: boolean;
		nextAfterId: number | null;
	};
}
